import React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { Container, Typography, Box, Table, TableBody, TableCell, 
         TableContainer, TableHead, TableRow, Paper, IconButton, 
         TablePagination, Dialog, DialogTitle, DialogContent, 
         DialogActions, TextField, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

export default function Users() {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalUsers, setTotalUsers] = useState(0);

    const fetchUsers = useCallback(async () => {
        try {
            const { data } = await axios.get(`/api/users?page=${page + 1}&limit=${rowsPerPage}`);
            setUsers(data.users);
            setTotalUsers(data.totalUsers);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    }, [page, rowsPerPage]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleClose = () => {
        setSelectedUser(null);
        setOpen(false);
    };

    const handleSave = async () => {
        try {
            if (selectedUser) {
                await axios.put(`/api/users/${selectedUser.id}`, selectedUser);
            }
            fetchUsers();
            handleClose();
        } catch (err) {
            console.error('Error saving user:', err);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await axios.delete(`/api/users/${id}`);
            fetchUsers();
        } catch (err) {
            console.error('Error deleting user:', err);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 50));
        setPage(0);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                User Management
            </Typography>
            <Box>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Username</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.role}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => {
                                            setSelectedUser(user);
                                            setOpen(true);
                                        }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(user.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[50, 100, 250]}
                        component="div"
                        count={totalUsers}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </Box>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        value={selectedUser?.username || ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser!, username: e.target.value })}
                    />
                    <TextField
                        label="Email"
                        fullWidth
                        margin="normal"
                        value={selectedUser?.email || ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser!, email: e.target.value })}
                    />
                    <TextField
                        label="Role"
                        fullWidth
                        margin="normal"
                        value={selectedUser?.role || ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser!, role: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}